/** Sanitize and validate currency amount inputs (max 2 decimal places). */

import { currencyDisplaySymbol, normalizeMatterCurrency } from './matterCurrency.js';

export function sanitizeCurrencyInput(raw) {
  let value = String(raw ?? '').replace(/[^\d.]/g, '');
  if (!value) return '';

  const firstDot = value.indexOf('.');
  if (firstDot !== -1) {
    value =
      value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, '');
  }

  const [whole, fraction] = value.split('.');
  if (fraction != null) {
    return `${whole}.${fraction.slice(0, 2)}`;
  }
  return whole;
}

/** Strip currency symbols / grouping commas, then sanitize to a plain amount string. */
export function parseCurrencyInputValue(raw) {
  return sanitizeCurrencyInput(
    String(raw ?? '')
      .replace(/[^\d.,]/g, '')
      .replace(/,/g, '')
  );
}

/**
 * Display amount while typing stays editable.
 * Pass a currency code to prefix the matching symbol; set withSymbol:false for
 * compound amount+currency controls that show the code separately.
 * @param {unknown} raw
 * @param {unknown} [currencyOrOptions='USD']
 * @param {{ withSymbol?: boolean }} [maybeOptions]
 */
export function formatCurrencyInputDisplay(raw, currencyOrOptions = 'USD', maybeOptions) {
  let currency = 'USD';
  let withSymbol = true;
  if (
    currencyOrOptions &&
    typeof currencyOrOptions === 'object' &&
    !Array.isArray(currencyOrOptions)
  ) {
    currency = currencyOrOptions.currency || 'USD';
    withSymbol = currencyOrOptions.withSymbol !== false;
  } else {
    currency = currencyOrOptions;
    if (maybeOptions && typeof maybeOptions === 'object') {
      withSymbol = maybeOptions.withSymbol !== false;
    }
  }

  const cleaned = parseCurrencyInputValue(raw);
  if (!cleaned) return '';
  const hasDot = cleaned.includes('.');
  const [wholeRaw, fraction] = cleaned.split('.');
  const whole = String(wholeRaw || '0').replace(/^0+(?=\d)/, '') || '0';
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const amountText =
    !hasDot
      ? grouped
      : fraction == null || fraction === ''
        ? `${grouped}.`
        : `${grouped}.${fraction.slice(0, 2)}`;
  if (!withSymbol) return amountText;
  const symbol = currencyDisplaySymbol(normalizeMatterCurrency(currency));
  return `${symbol}${amountText}`;
}

export function isValidCurrencyAmount(raw) {
  const text = String(raw ?? '')
    .replace(/[^\d.,]/g, '')
    .replace(/,/g, '')
    .trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return false;
  const amount = Number(text);
  return Number.isFinite(amount) && amount > 0;
}

/**
 * Normalize a committed amount for form storage / sync (always two decimals).
 * Returns '' for empty/invalid input so callers can skip updating the field.
 * @param {unknown} raw
 * @returns {string}
 */
export function commitCurrencyAmount(raw) {
  const cleaned = parseCurrencyInputValue(raw);
  if (!cleaned || cleaned.endsWith('.')) return '';
  const amount = Number(cleaned);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return amount.toFixed(2);
}
